import { useEffect, useRef } from "react";
import { mdToHtml } from "../utils/markdown";
import CopyButton, { plainText, writeClipboard } from "./CopyButton";

/** A table as the reader sees it, tab-separated, so it pastes into Excel as real columns. */
function tableAsTsv(table) {
  return [...table.rows]
    .map((row) => [...row.cells].map((c) => (c.innerText ?? c.textContent ?? "").replace(/[\t\r\n]+/g, " ").trim()).join("\t"))
    .join("\n");
}

/**
 * One assistant answer: the rendered markdown, an always-visible copy icon under it once the answer is
 * complete, and a copy icon on every table inside it.
 *
 * The table buttons are added to the rendered HTML after it mounts, because the bubble is markdown turned
 * into HTML (dangerouslySetInnerHTML) and its tables are not React elements. They are added once the
 * answer is complete, so a table still streaming in is never copied half-written.
 */
export default function AnswerBubble({ markdown, done = true }) {
  const ref = useRef(null);
  const html = mdToHtml(markdown);

  useEffect(() => {
    const el = ref.current;
    if (!el || !done) return undefined;
    for (const wrap of el.querySelectorAll(".tbl-wrap")) {
      if (wrap.querySelector(".copy-btn.on-table")) continue;
      const table = wrap.querySelector("table");
      if (!table) continue;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "copy-btn on-table";
      btn.title = "Copy table (pastes into Excel)";
      btn.setAttribute("aria-label", btn.title);
      btn.textContent = "⧉";
      btn.addEventListener("click", async (e) => {
        e.stopPropagation();
        let ok = true;
        try {
          await writeClipboard(tableAsTsv(table));
        } catch {
          ok = false;
        }
        btn.textContent = ok ? "✓" : "✗";
        btn.title = ok ? "Copied" : "Couldn't copy";
        btn.classList.toggle("done", ok);
        setTimeout(() => {
          btn.textContent = "⧉";
          btn.title = "Copy table (pastes into Excel)";
          btn.classList.remove("done");
        }, 1500);
      });
      wrap.prepend(btn);
    }
    return undefined; // the ✓ -> ⧉ timers are left to run: clearing them on a re-render would leave ✓ stuck
    // No dependency list on purpose: runs after EVERY render. A re-render can re-write the bubble's HTML
    // (it is dangerouslySetInnerHTML), which drops the injected buttons; re-checking each time puts them
    // back, and the "already has one" check above keeps it from ever adding a second.
  });

  return (
    <div className="bubble-wrap">
      <div ref={ref} className="bubble" dangerouslySetInnerHTML={{ __html: html }} />
      {done && (
        <div className="bubble-actions">
          <CopyButton title="Copy answer" getText={() => plainText(markdown)} />
        </div>
      )}
    </div>
  );
}
